import { isPlatformBrowser, SlicePipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, makeStateKey, OnInit, PLATFORM_ID, TransferState } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import { RouterLink } from "@angular/router";
import { Revista } from "@src/app/models/interfaces/revista.interface";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";
import { FooterService } from "@src/app/servicios/footer.service";
import { RevistaService } from "@src/app/servicios/revista/revista.service";
import { first, firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { GfkService } from "src/app/servicios/gfk/gfk.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { PublicidadService } from "src/app/servicios/publicidad/publicidad.service";
import { TitleWrapperService } from "src/app/servicios/title-wrapper/title-wrapper.service";

// Llave para la transferencia de estado (SSR)
const TRANSFER_KEY = makeStateKey<Revista[]>("listado-revistas");

@Component({
  selector: "app-revista",
  templateUrl: "./revista.component.html",
  styleUrls: ["./revista.component.css"],
  imports: [RouterLink, SlicePipe],
  providers: [RevistaService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevistaComponent implements OnInit {
  // Variables principales
  constantesGlobales: any;
  revistas: Revista[] = [];
  particionPublicidad: number;
  publicidad_bloques = {};
  publicidad_divisores_movil = [];
  Math = Math;

  constructor(
    private readonly revistaService: RevistaService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly platformService: PlatformService,
    private readonly gfkService: GfkService,
    private readonly publiService: PublicidadService,
    private readonly configService: ConfigService,
    private readonly transferState: TransferState,
    private readonly titleService: TitleWrapperService,
    private readonly cd: ChangeDetectorRef,
    private readonly canonicalService: CanonicalService,
    private readonly footerService: FooterService,
  ) {
    this.configService.getConstantesGlobales();
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  /**
   * Inicializa el componente:
   * - Obtiene las revistas.
   * - Inicia GFK si está activado.
   * - Obtiene la publicidad.
   */
  ngOnInit(): void {
    this.footerService.isPaginationFinished.set(true);
    this.getRevistas();
    if (this.constantesGlobales.gfk === true) {
      if (isPlatformBrowser(this.platformId)) {
        this.initGfk();
      }
    }
    this.getPublicidad();
  }

  /**
   * Obtiene las revistas usando la transferencia de estado si está disponible.
   */
  getRevistas(): void {
    if (!this.platformService.isBrowser()) {
      this.fetchRevistasAndStoreKey();
    } else {
      if (this.transferState.hasKey(TRANSFER_KEY)) {
        const transferData = this.transferState.get<Revista[]>(TRANSFER_KEY, null);
        this.setRevistas(transferData);
        this.transferState.remove(TRANSFER_KEY);
      } else {
        this.fetchRevistasAndStoreKey();
      }
    }
  }

  /**
   * Recupera las revistas desde el servicio y las almacena en el TransferState.
   */
  async fetchRevistasAndStoreKey(): Promise<void> {
    const idAmbito = this.constantesGlobales.idAmbito;
    const magazine = this.revistaService.getRevistas(idAmbito);
    if (magazine) {
      const result = await firstValueFrom(magazine);
      this.setRevistas(result);
      this.transferState.set(TRANSFER_KEY, result);
    }
  }

  /**
   * Establece las revistas, calcula la partición de publicidad y actualiza el título y meta tags.
   * @param result Resultado obtenido del servicio.
   */
  setRevistas(result: Revista[]): void {
    this.revistas = result;
    this.particionPublicidad = 2 * Math.floor(this.revistas.length / 6);
    this.titleService.setTitle("Revista más Tribuna - " + this.constantesGlobales.siteName);
    this.updateMetaTags();
    this.cd.detectChanges();
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante para SEO y redes sociales.
   *
   * Este método configura un array de metaetiquetas que incluye propiedades como descripción,
   * título, tipo de contenido, imagen compartida, URL, autor, editor, y otros metadatos
   * relacionados con Dublin Core. Además, actualiza las URLs canónicas utilizando el servicio
   * `canonicalService`.
   *
   * @private
   */
  private updateMetaTags(): void {
    const metaTagsArray: MetaDefinition[] = [
      { name: "description", content: "Más Tribuna, una nueva revista que analiza un sector clave para el desarrollo de Castilla y León" },
      { property: "og:title", content: "Revista Más Tribuna - " + this.constantesGlobales.siteName },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.imgShare}` },
      { property: "og:url", content: this.constantesGlobales.siteDomain },
      { property: "og:description", content: "Más Tribuna, una nueva revista que analiza un sector clave para el desarrollo de Castilla y León" },
      { property: "article:author", content: this.constantesGlobales.siteName },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: this.constantesGlobales.siteTitle },
      { name: "DC.creator", content: this.constantesGlobales.siteTitle },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: this.constantesGlobales.metaTag },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: new Date() },
    ];
    this.canonicalService.updateCanonicalUrls(metaTagsArray);
  }

  /**
   * Obtiene la publicidad y la organiza en bloques y divisores para móvil.
   */
  getPublicidad(): void {
    const publi = this.publiService.getPubli();
    if (publi) {
      publi.pipe(first()).subscribe((result) => {
        if (result != null) {
          result.imagenes.forEach((currentValue) => {
            if ((currentValue.idPosicion === "27" || currentValue.idPosicion === "28" || currentValue.idPosicion === "29") && (currentValue.id_tipo === "40" || currentValue.id_tipo === "41" || currentValue.id_tipo === "42" || currentValue.id_tipo === "43" || currentValue.id_tipo === "44")) {
              this.publicidad_bloques[currentValue.nombrePosicion.replace("Divisor ", "")] = currentValue;
            }
            if (currentValue.idPosicion === "34" || currentValue.idPosicion === "35" || currentValue.idPosicion === "36" || currentValue.idPosicion === "37" || currentValue.idPosicion === "38" || currentValue.idPosicion === "39" || currentValue.idPosicion === "40" || currentValue.idPosicion === "41" || currentValue.idPosicion === "42" || currentValue.idPosicion === "43" || currentValue.idPosicion === "44" || currentValue.idPosicion === "45" || currentValue.idPosicion === "46" || currentValue.idPosicion === "47" || currentValue.idPosicion === "48") {
              this.publicidad_divisores_movil[currentValue.nombrePosicion.replace("Movil - divisor fila ", "")] = currentValue;
            }
          });
        }
      });
    }
  }

  /**
   * Inicializa GFK (medición de audiencias) con parámetros personalizados.
   */
  private initGfk(): void {
    if (this.constantesGlobales.gfk) {
      const customParams = {
        c1: this.stripProtocolAndSubdomain(this.constantesGlobales.siteDomain),
        c2: "revistas",
      };
      this.gfkService.impression(customParams);
    }
  }

  /**
   * Elimina el protocolo y subdominio de una URL.
   * @param url URL a limpiar.
   * @returns URL sin protocolo ni subdominio.
   */
  stripProtocolAndSubdomain(url: string): string {
    const strippedUrl = url.replace(/^(https?:\/\/)?(www\.)?/, "");
    return strippedUrl;
  }
}
